import React from 'react';
import { Receipt, Building2, User, Mail, MapPin, CheckCircle2 } from 'lucide-react';
import { Fn } from '../utils/rutValidation';

/**
 * Sección reutilizable de Datos de Facturación / Boleta
 * Permite emitir la boleta al mismo solicitante (persona natural)
 * o bien a un tercero / empresa (RUT >= 50.000.000 o diferente).
 */
export default function FacturacionFormSection({
  billingData,
  onChange,
  solicitanteData = {},
  isDarkTheme = false
}) {
  const {
    facturacion_misma_persona = true,
    boleta_rut = '',
    boleta_razon_social = '',
    boleta_giro = '',
    boleta_direccion = '',
    boleta_correo = ''
  } = billingData;

  const handleToggleMismaPersona = (e) => {
    const checked = e.target.checked;
    onChange({
      ...billingData,
      facturacion_misma_persona: checked,
      // Si se marca, sincronizamos automáticamente con el solicitante
      ...(checked ? {
        boleta_rut: solicitanteData.rut_solicitante || '',
        boleta_razon_social: solicitanteData.nombre_solicitante || '',
        boleta_giro: '',
        boleta_direccion: solicitanteData.direccion || '',
        boleta_correo: solicitanteData.correo || solicitanteData.email || ''
      } : {})
    });
  };

  const handleFieldChange = (field, value) => {
    onChange({
      ...billingData,
      [field]: value
    });
  };

  const isEmpresaRut = boleta_rut ? Fn.esEmpresa(boleta_rut) : false;

  return (
    <div style={{
      marginTop: '1.5rem',
      marginBottom: '1.5rem',
      padding: '1.25rem',
      borderRadius: '10px',
      background: isDarkTheme ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
      border: `1px solid ${isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '1rem' }}>
        <h4 style={{
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '1rem',
          fontWeight: 700,
          color: isDarkTheme ? '#f1f5f9' : 'var(--primary-dark, #0f172a)'
        }}>
          <Receipt size={18} color="var(--primary, #0284c7)" />
          Datos de Facturación / Boleta
        </h4>
        <span style={{ fontSize: '0.75rem', color: isDarkTheme ? '#94a3b8' : '#64748b' }}>
          Documento tributario legal
        </span>
      </div>

      {/* Checkbox "Usar datos del requiriente para la boleta" */}
      <label style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '0.75rem 1rem',
        borderRadius: '8px',
        background: facturacion_misma_persona
          ? (isDarkTheme ? 'rgba(14, 165, 233, 0.12)' : '#e0f2fe')
          : (isDarkTheme ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'),
        border: `1px solid ${facturacion_misma_persona ? 'var(--primary, #0284c7)' : (isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : '#cbd5e1')}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        marginBottom: '1rem'
      }}>
        <input
          type="checkbox"
          checked={facturacion_misma_persona}
          onChange={handleToggleMismaPersona}
          style={{ width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer', accentColor: 'var(--primary, #0284c7)' }}
        />
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '0.9rem',
            fontWeight: 600,
            color: facturacion_misma_persona ? (isDarkTheme ? '#38bdf8' : '#0369a1') : (isDarkTheme ? '#e2e8f0' : '#1e293b')
          }}>
            Usar datos del requiriente / solicitante para la boleta
          </div>
          <div style={{ fontSize: '0.8rem', color: isDarkTheme ? '#94a3b8' : '#64748b', marginTop: '2px' }}>
            {facturacion_misma_persona
              ? 'La boleta se emitirá a nombre del solicitante. Desmarque si requiere emitirla a nombre de una empresa o un tercero.'
              : 'Complete a continuación los datos de la empresa o tercero a quien se emitirá la boleta.'}
          </div>
        </div>
      </label>

      {/* Campos de Facturación cuando no es la misma persona */}
      {!facturacion_misma_persona && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1rem',
          paddingTop: '0.5rem',
          animation: 'fadeIn 0.2s ease-in-out'
        }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: isDarkTheme ? '#cbd5e1' : '#334155', marginBottom: '0.35rem' }}>
              RUT Boleta / Empresa *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Ej: 76.123.456-7"
                value={boleta_rut}
                onChange={e => handleFieldChange('boleta_rut', e.target.value)}
                required={!facturacion_misma_persona}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${isDarkTheme ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1'}`,
                  background: isDarkTheme ? 'rgba(0, 0, 0, 0.2)' : '#ffffff',
                  color: isDarkTheme ? '#ffffff' : '#0f172a',
                  fontSize: '0.9rem'
                }}
              />
            </div>
            {isEmpresaRut && (
              <span style={{ fontSize: '0.72rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                <Building2 size={12} /> RUT de Empresa reconocido
              </span>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: isDarkTheme ? '#cbd5e1' : '#334155', marginBottom: '0.35rem' }}>
              Razón Social o Nombre Completo *
            </label>
            <input
              type="text"
              placeholder="Ej: Inversiones Los Andes SpA"
              value={boleta_razon_social}
              onChange={e => handleFieldChange('boleta_razon_social', e.target.value)}
              required={!facturacion_misma_persona}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: '6px',
                border: `1px solid ${isDarkTheme ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1'}`,
                background: isDarkTheme ? 'rgba(0, 0, 0, 0.2)' : '#ffffff',
                color: isDarkTheme ? '#ffffff' : '#0f172a',
                fontSize: '0.9rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: isDarkTheme ? '#cbd5e1' : '#334155', marginBottom: '0.35rem' }}>
              Giro Comercial {isEmpresaRut ? '*' : '(opcional para persona)'}
            </label>
            <input
              type="text"
              placeholder="Ej: Inmobiliaria, Servicios..."
              value={boleta_giro}
              onChange={e => handleFieldChange('boleta_giro', e.target.value)}
              required={!facturacion_misma_persona && isEmpresaRut}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: '6px',
                border: `1px solid ${isDarkTheme ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1'}`,
                background: isDarkTheme ? 'rgba(0, 0, 0, 0.2)' : '#ffffff',
                color: isDarkTheme ? '#ffffff' : '#0f172a',
                fontSize: '0.9rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: isDarkTheme ? '#cbd5e1' : '#334155', marginBottom: '0.35rem' }}>
              Correo de Facturación *
            </label>
            <input
              type="email"
              placeholder="facturacion@empresa.cl"
              value={boleta_correo}
              onChange={e => handleFieldChange('boleta_correo', e.target.value)}
              required={!facturacion_misma_persona}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: '6px',
                border: `1px solid ${isDarkTheme ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1'}`,
                background: isDarkTheme ? 'rgba(0, 0, 0, 0.2)' : '#ffffff',
                color: isDarkTheme ? '#ffffff' : '#0f172a',
                fontSize: '0.9rem'
              }}
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: isDarkTheme ? '#cbd5e1' : '#334155', marginBottom: '0.35rem' }}>
              Dirección Tributaria *
            </label>
            <input
              type="text"
              placeholder="Ej: Av. Providencia 1234, Of. 501, Santiago"
              value={boleta_direccion}
              onChange={e => handleFieldChange('boleta_direccion', e.target.value)}
              required={!facturacion_misma_persona}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: '6px',
                border: `1px solid ${isDarkTheme ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1'}`,
                background: isDarkTheme ? 'rgba(0, 0, 0, 0.2)' : '#ffffff',
                color: isDarkTheme ? '#ffffff' : '#0f172a',
                fontSize: '0.9rem'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
